// import { AdminLoginRequest, AdminLoginResponse } from "@/types/admin/auth";

// const API_BASE_URL =
//   process.env.NEXT_PUBLIC_API_URL || "https://www.lilylanka.com";

// export const adminAuthService = {
//   async refresh(refreshToken: string): Promise<AdminLoginResponse> {
//     const response = await fetch(`${API_BASE_URL}/api/admin/auth/refresh`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         Accept: "application/json",
//       },
//       body: JSON.stringify({ refreshToken }),
//     });

//     if (!response.ok) {
//       throw new Error("Session refresh failed");
//     }

//     return response.json();
//   },

//   async login(credentials: AdminLoginRequest): Promise<AdminLoginResponse> {
//     const response = await fetch(`${API_BASE_URL}/api/admin/auth/login`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         Accept: "application/json",
//       },
//       body: JSON.stringify(credentials),
//     });

//     if (!response.ok) {
//       const errorText = await response.text();
//       console.error("Login failed:", response.status, errorText);
//       throw new Error("Invalid credentials");
//     }

//     return response.json();
//   },
// };



import { AdminLoginRequest, AdminLoginResponse } from "@/types/admin/auth";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || "https://www.lilylanka.com";

export const adminAuthService = {
  async refresh(refreshToken: string): Promise<AdminLoginResponse> {
    const response = await fetch(`${API_BASE_URL}/api/admin/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      throw new Error("Session refresh failed");
    }

    return response.json();
  },

  async login(credentials: AdminLoginRequest): Promise<AdminLoginResponse> {
    const response = await fetch(`${API_BASE_URL}/api/admin/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Login failed:", response.status, errorText);
      throw new Error("Invalid credentials");
    }

    return response.json();
  },
};

// ===================================================
// --- ADMIN TOKEN PERSISTENCE + AUTO-REFRESH ---
// ===================================================
// NOTE: assumes AdminLoginResponse has accessToken, refreshToken, and
// expiresIn (seconds) fields, matching the shape of every other login
// response in this codebase (CustomerLoginResponse, KeycloakTokenResponse).
// If types/admin/auth.ts differs, adjust the field names below.

let adminRefreshTimer: ReturnType<typeof setTimeout> | null = null;

function setAdminCookie(token: string) {
  const expires = new Date(Date.now() + 8 * 60 * 60 * 1000).toUTCString();
  document.cookie = `adminAccessToken=${token}; path=/; expires=${expires}; SameSite=Strict`;
}

/**
 * Stores tokens from admin login/refresh and schedules the next silent
 * refresh ~30s before this access token expires. Call once right after a
 * successful login, and once on app start if a refreshToken already exists
 * in localStorage (so refreshing survives a page reload/navigation).
 */
export function persistAdminTokens(data: AdminLoginResponse) {
  const accessToken = (data as any).accessToken as string;
  const refreshToken = (data as any).refreshToken as string;
  const expiresIn = ((data as any).expiresIn as number) ?? 300;

  localStorage.setItem("adminAccessToken", accessToken);
  localStorage.setItem("adminRefreshToken", refreshToken);
  setAdminCookie(accessToken);

  if (adminRefreshTimer) clearTimeout(adminRefreshTimer);

  const refreshInMs = Math.max((expiresIn - 30) * 1000, 5000);
  adminRefreshTimer = setTimeout(async () => {
    const rt = localStorage.getItem("adminRefreshToken");
    if (!rt) return;
    try {
      const res = await adminAuthService.refresh(rt);
      persistAdminTokens(res);
    } catch {
      // Refresh token itself expired/invalid — clear so the next API
      // call's 401 (or the layout's token check) sends the admin back
      // to sign in cleanly.
      clearAdminTokens();
    }
  }, refreshInMs);
}

/** Call on every admin page mount/navigation to resume refreshing (in-memory timer doesn't survive navigation without this). */
export function resumeAdminTokenRefresh() {
  const rt = localStorage.getItem("adminRefreshToken");
  const token = localStorage.getItem("adminAccessToken");
  if (!rt || !token) return;
  if (adminRefreshTimer) return; // already scheduled this session
  // Unknown remaining lifetime — refresh soon rather than trust an
  // unknown TTL.
  persistAdminTokens({
    accessToken: token,
    refreshToken: rt,
    expiresIn: 30,
  } as AdminLoginResponse);
}

/** Clear stored admin tokens and cancel any pending refresh. Call on logout. */
export function clearAdminTokens() {
  if (adminRefreshTimer) {
    clearTimeout(adminRefreshTimer);
    adminRefreshTimer = null;
  }
  localStorage.removeItem("adminAccessToken");
  localStorage.removeItem("adminRefreshToken");
  document.cookie =
    "adminAccessToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Strict";
}